import { useTranslation } from '../../../i18n';
import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Form, Input, Modal, Popconfirm, Select, Space, Tag, Typography } from 'antd';
import { DeleteOutlined, PlusOutlined, ReloadOutlined, RollbackOutlined } from '@ant-design/icons';
import CompactTable from './CompactTable';
import AutoBackupCard from './AutoBackupCard';
import SnapshotSchedule from './SnapshotSchedule';
import { formatDateTime } from '../../../utils/date';
import { ok, runAction, toArray } from './utils';

const { Text } = Typography;

const SnapshotBackupTab = ({ id, vm, api, adapter, isMobile, disabled, active, onInvoice }) => {
    const { t } = useTranslation();
    const [snapshots, setSnapshots] = useState([]);
    const [snapLoading, setSnapLoading] = useState(false);
    // legacy local qcow2 backups: shown read-only (restore/delete) only while rows exist
    const [backups, setBackups] = useState([]);
    const [backupLoading, setBackupLoading] = useState(false);
    const [snapOpen, setSnapOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [snapForm] = Form.useForm();

    const loadSnapshots = useCallback(async () => {
        setSnapLoading(true);
        try {
            const res = await adapter.getSnapshots(id);
            setSnapshots(ok(res) ? toArray(res.data, 'snapshots') : []);
        } finally {
            setSnapLoading(false);
        }
    }, [adapter, id]);

    const loadBackups = useCallback(async () => {
        setBackupLoading(true);
        try {
            const res = await api.getVmBackups(id);
            if (ok(res)) {
                setBackups(toArray(res.data, 'items'));
            } else {
                setBackups([]);
            }
        } finally {
            setBackupLoading(false);
        }
    }, [api, id]);

    useEffect(() => {
        if (!active) return;
        loadSnapshots();
        loadBackups();
    }, [active, loadSnapshots, loadBackups]);

    // ---- snapshots ----
    const createSnapshot = async (v) => {
        setSubmitting(true);
        const res = await runAction(() => adapter.createSnapshot(id, v.disk, v.description || ''), t('Snapshot creation submitted'), t('Failed to create snapshot'));
        setSubmitting(false);
        if (res) {
            setSnapOpen(false);
            loadSnapshots();
        }
    };
    const recoverSnapshot = (s) => Modal.confirm({
        title: t('Restore snapshot {{name}}?', { name: s.name }),
        content: t('Existing data on the disk will be overwritten.'),
        okText: t('Restore'),
        okButtonProps: { danger: true },
        cancelText: t('Cancel'),
        onOk: async () => { if (await runAction(() => api.recoverSnapshot(id, s.name), t('Snapshot restore submitted'), t('Failed to restore snapshot'))) loadSnapshots(); },
    });
    const deleteSnapshot = async (s) => {
        if (await runAction(() => api.deleteSnapshot(id, s.name), t('Snapshot deleted'), t('Failed to delete snapshot'))) loadSnapshots();
    };

    // ---- backups ----
    const restoreBackup = (b) => Modal.confirm({
        title: t('Restore from backup {{value}}?', { value: b.name || `#${b.id}` }),
        content: t('The disk will be overwritten, and the cloud server will restart.'),
        okText: t('Restore'),
        okButtonProps: { danger: true },
        cancelText: t('Cancel'),
        onOk: async () => { if (await runAction(() => api.restoreVmBackup(id, b.id), t('Restore task submitted'), t('Restore failed'))) loadBackups(); },
    });
    const deleteBackup = async (b) => {
        if (await runAction(() => api.deleteVmBackup(id, b.id), t('Backup deleted'), t('Deletion failed'))) loadBackups();
    };

    const snapQuota = Number(vm.snapshot_num) || 0;
    const snapFull = snapQuota > 0 && snapshots.length >= snapQuota;

    const snapColumns = [
        { title: t('Name'), key: 'name', render: (_, s) => <Text>{s.name}</Text> },
        { title: t('Disk'), key: 'disk', render: (_, s) => s.disk || '-' },
        { title: t('Created at'), key: 'created_at', render: (_, s) => formatDateTime(s.created_at || s.create_at) },
        { title: t('Notes'), key: 'description', render: (_, s) => (s.schedule_id ? <Tag color="blue">{t('Auto')}</Tag> : (s.description || '-')) },
        {
            title: '', key: 'actions', align: 'right', render: (_, s) => (
                <Space size={4}>
                    <Button size="small" type="text" icon={<RollbackOutlined />} disabled={disabled} onClick={() => recoverSnapshot(s)}>{t('Restore')}</Button>
                    <Popconfirm title={t('Delete this snapshot?')} okButtonProps={{ danger: true }} onConfirm={() => deleteSnapshot(s)} disabled={disabled}>
                        <Button size="small" type="text" danger icon={<DeleteOutlined />} disabled={disabled}>{t('Delete')}</Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const backupColumns = [
        { title: t('Name'), key: 'name', render: (_, b) => b.name || `#${b.id}` },
        { title: t('Disk'), key: 'disk', render: (_, b) => b.disk || '-' },
        {
            title: t('Status'), key: 'status', render: (_, b) => (Number(b.status) === 1
                ? <Tag color="success">{t('Done')}</Tag>
                : <Tag color="processing">{t('In progress')}</Tag>),
        },
        { title: t('Created at'), key: 'create_at', render: (_, b) => formatDateTime(b.create_at || b.created_at) },
        {
            title: '', key: 'actions', align: 'right', render: (_, b) => {
                const done = Number(b.status) === 1;
                return (
                    <Space size={4}>
                        <Button size="small" type="text" icon={<RollbackOutlined />} disabled={disabled || !done} onClick={() => restoreBackup(b)}>{t('Restore')}</Button>
                        <Popconfirm title={t('Delete this backup?')} okButtonProps={{ danger: true }} onConfirm={() => deleteBackup(b)} disabled={disabled || !done}>
                            <Button size="small" type="text" danger icon={<DeleteOutlined />} disabled={disabled || !done}>{t('Delete')}</Button>
                        </Popconfirm>
                    </Space>
                );
            },
        },
    ];

    const quotaText = (used, quota) => <Text type="secondary" style={{ fontSize: 12, fontWeight: 'normal' }}>{used} / {quota}</Text>;

    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Card
                size="small"
                title={<Space size={8}>{t('Snapshot')}{snapQuota > 0 && quotaText(snapshots.length, snapQuota)}</Space>}
                extra={(
                    <Space size={4}>
                        <Button size="small" type="text" icon={<ReloadOutlined />} onClick={loadSnapshots} loading={snapLoading} />
                        <SnapshotSchedule id={id} api={api} disabled={disabled} active={active} onChanged={loadSnapshots} />
                        <Button
                            size="small"
                            type="primary"
                            icon={<PlusOutlined />}
                            disabled={disabled || snapFull}
                            title={snapFull ? t('Quota reached') : undefined}
                            onClick={() => { snapForm.resetFields(); snapForm.setFieldsValue({ disk: (vm.disks || [])[0]?.id }); setSnapOpen(true); }}
                        >
                            {t('Create snapshot')}
                        </Button>
                    </Space>
                )}
            >
                <CompactTable isMobile={isMobile} columns={snapColumns} dataSource={snapshots} loading={snapLoading} emptyText={t('No snapshots yet')} />
            </Card>

            <AutoBackupCard id={id} api={api} adapter={adapter} isMobile={isMobile} disabled={disabled} active={active} onInvoice={onInvoice} />

            {backups.length > 0 && (
                <Card
                    size="small"
                    title={t('Local backups (legacy)')}
                    extra={<Button size="small" type="text" icon={<ReloadOutlined />} onClick={loadBackups} loading={backupLoading} />}
                >
                    <CompactTable isMobile={isMobile} columns={backupColumns} dataSource={backups} loading={backupLoading} emptyText={t('No backups yet')} />
                </Card>
            )}

            <Modal title={t('Create snapshot')} open={snapOpen} onCancel={() => setSnapOpen(false)} onOk={() => snapForm.submit()} confirmLoading={submitting} destroyOnClose>
                <Form form={snapForm} layout="vertical" onFinish={createSnapshot}>
                    <Form.Item name="disk" label={t('Disk')} rules={[{ required: true, message: t('Select a disk') }]}>
                        <Select
                            options={(vm.disks || []).map((d) => ({
                                value: d.id,
                                label: `${d.dev || d.id} · ${d.size} GB${d.type === 'system' ? t('(System disk)') : ''}`,
                            }))}
                        />
                    </Form.Item>
                    <Form.Item name="description" label={t('Notes')} style={{ marginBottom: 0 }}>
                        <Input maxLength={100} />
                    </Form.Item>
                </Form>
            </Modal>
        </Space>
    );
};

export default SnapshotBackupTab;
